import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { Button } from 'primereact/button';
import { Checkbox, CheckboxChangeParams } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Panel } from 'primereact/panel';
import { TabPanel, TabView, TabViewTabChangeParams } from 'primereact/tabview';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';

import { Product } from '../../../../../enums/product';
import useAxiosHook from '../../../../../hooks/useAxiosHook';
import usePrevious from '../../../../../hooks/usePrevious';
import { HubCollection } from '../../../../../types/api/hubs';
import { PlaceResource } from '../../../../../types/api/places';
import { invalidTabHeaderIcon } from '../../../../../utils/globals';
import { cleanUpObject } from '../../../../../utils/helpers/object';
import FieldWithErrors from '../../../../Forms/ReactHookForm/FieldWithErrors';
import { SinglePayment, toCalculatorData } from './Calculator.functions';
import {
  ConstantFields,
  FormValues,
  OrdererType,
  attributesTabFields,
  ordererTabFields,
  recipientTabFields,
  senderTabFields,
} from './CreateEditDialog.functions';
import AttributesTab from './Tabs/Attributes/AttributesTab';
import OrdererTab from './Tabs/OrdererTab';
import PaymentsTab from './Tabs/PaymentsTab';
import { PickupAssignmentTab } from './Tabs/PickupAssignmentTab';
import RecipientTab from './Tabs/RecipientTab';
import SenderTab from './Tabs/SenderTab';

type Props = {
  constantFields: ConstantFields;
  isEditDialog: boolean;
  isTabs: boolean;
  isWarehouseReception?: boolean;
  isSenderTabDisabled: any;
  orderIndex?: number;
};

function FormContainer({
  constantFields,
  isEditDialog,
  isTabs,
  isWarehouseReception,
  isSenderTabDisabled,
  orderIndex,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [active, setActive] = useState<OrdererType>('sender');
  const [activeTab, setActiveTab] = useState(0);

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();
  const values = watch();
  console.log('values ', values);

  const calculatorPayloadData = useMemo(
    () => cleanUpObject(toCalculatorData(values)),
    [values]
  );

  const isOrdererDisplayed = values?.orderer === 3;

  const { data: hubsData } = useAxiosHook<HubCollection>('/hubs');

  const initialCalculatorData = useMemo(
    () => cleanUpObject(toCalculatorData(control._defaultValues as any)),
    [control]
  );

  const isPaymentsEqualToItsDefaultValue = useMemo(() => {
    if (!isEditDialog) {
      return false;
    }

    return _.isEqual(calculatorPayloadData, initialCalculatorData);
  }, [calculatorPayloadData, initialCalculatorData, isEditDialog]);

  const { data: calculatorData, isLoading: isCalculatorDataLoading } =
    useAxiosHook<SinglePayment[]>(
      {
        url: '/orders/calculator',
        method: 'POST',
        data: calculatorPayloadData,
      },
      {
        skipWhen:
          !values.pickup_address?.placeName ||
          !values.delivery_address?.placeName ||
          isPaymentsEqualToItsDefaultValue,
      }
    );

  const previousCalculatorData = usePrevious(calculatorData);

  useEffect(() => {
    if (!calculatorData || previousCalculatorData === calculatorData) {
      return;
    }

    setValue(
      'payments',
      calculatorData.map((payment) => ({
        ...payment,
        editedPrice: payment.unit_price,
      }))
    );
  }, [calculatorData, previousCalculatorData, setValue]);

  useEffect(() => {
    if (!isPaymentsEqualToItsDefaultValue) {
      return;
    }

    setValue('payments', control._defaultValues.payments as any);
  }, [control, isPaymentsEqualToItsDefaultValue, setValue]);

  const { data: ordererLocationData } = useAxiosHook<PlaceResource>(
    {
      url: `/places/${values?.klient_naracatel_id}`,
    },
    { skipWhen: !values?.klient_naracatel_id, skipCache: true }
  );

  useEffect(() => {
    setValue('ordererName', ordererLocationData?.ime ?? '');
  }, [ordererLocationData, setValue]);

  const debouncedDeliveryPlaceId = useDebounce(
    values.delivery_address?.placeId,
    500
  );
  const debouncedDeliveryStreetName = useDebounce(
    values.delivery_address?.streetName,
    500
  );
  const debouncedDeliveryHouseNumber = useDebounce(
    values.delivery_address?.housenumber,
    500
  );

  const { data: nearestRecipientHub, isLoading: isLoadingNearestHub } =
    useAxiosHook<{
      ime: string;
      id: number;
    }>(
      {
        url: '/hubs/nearest',
        data: {
          placeId: debouncedDeliveryPlaceId,
          addressFrom: `${debouncedDeliveryStreetName}, ${debouncedDeliveryHouseNumber}`,
        },
        method: 'POST',
      },
      {
        skipWhen: !debouncedDeliveryPlaceId,
      }
    );

  const senderHeader = (
    <div className="p-d-flex p-jc-between p-ai-center">
      <div>{t('Sender')}</div>
      <div className="p-d-flex p-ai-center p-jc-around">
        <FieldWithErrors
          name="sender_orderer"
          label={false}
          className="p-d-flex p-mb-2 p-jc-between"
        >
          <Controller
            name="sender_orderer"
            render={({ field }) => (
              <>
                <Checkbox
                  id="sender_orderer"
                  name="sender_orderer"
                  value={field.value}
                  checked={values?.orderer === 1}
                  onChange={(e: CheckboxChangeParams) => {
                    if (e.checked) {
                      setValue('orderer', 1);
                    } else {
                      setValue('orderer', 2);
                    }
                  }}
                />
                <label className="p-mr-2 p-text-normal">
                  {t('Sender is orderer')}
                </label>
              </>
            )}
          />
        </FieldWithErrors>
      </div>
    </div>
  );

  const recipientHeader = (
    <div className="p-d-flex p-jc-between p-ai-center">
      <div>{t('Recipient')}</div>
      <div className="p-d-flex p-ai-center p-jc-around">
        <label className="p-mr-2 p-text-normal">
          {t('Recipient is orderer')}
        </label>
        <FieldWithErrors
          name="recipient_orderer"
          label={false}
          className="p-d-flex p-mb-2 p-jc-between"
        >
          <Controller
            name="recipient_orderer"
            render={({ field }) => (
              <Checkbox
                id="recipient_orderer"
                name="recipient_orderer"
                value={field.value}
                checked={values?.orderer === 2}
                onChange={(e: CheckboxChangeParams) => {
                  if (e.checked) {
                    setValue('orderer', 2);
                  } else {
                    setValue('orderer', 1);
                  }
                }}
              />
            )}
          />
        </FieldWithErrors>
      </div>
    </div>
  );

  const ordererHeader = (
    <div className="p-d-flex p-jc-between p-ai-center">
      <div>{t('Orderer')}</div>
      <div
        className="remove-orderer"
        onClick={() => {
          setValue('orderer', 1);
          setValue('klient_naracatel_id', values?.klient_od_id);
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </div>
    </div>
  );

  const attributesHeader = (
    <div className="p-d-flex p-jc-between p-ai-center">
      <div>{t('Attributes')}</div>
      <FieldWithErrors name="proizvod_id" label={false}>
        <Controller
          name="proizvod_id"
          render={({ field }) => (
            <Dropdown
              id="proizvod_id"
              name="proizvod_id"
              options={[
                { label: t('Package'), value: Product.Package },
                { label: t('Document'), value: Product.Document },
              ]}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              className="data-cy-proizvod_id"
            />
          )}
        />
      </FieldWithErrors>
    </div>
  );

  function isTabValid(TabObject: Partial<FormValues>) {
    for (let key in TabObject) {
      if (errors[key as keyof FormValues] && errors[key as keyof FormValues]) {
        return false;
      }
    }
    return true;
  }

  const senderTabValid = isTabValid(senderTabFields);
  const recipientTabValid = isTabValid(recipientTabFields);
  const ordererTabValid = isTabValid(ordererTabFields);
  const attributesTabValid = isTabValid(attributesTabFields);

  const senderTabIcon = senderTabValid ? {} : invalidTabHeaderIcon;
  const recipientTabIcon = recipientTabValid ? {} : invalidTabHeaderIcon;
  const ordererTabIcon = ordererTabValid ? {} : invalidTabHeaderIcon;
  const attributesTabIcon = attributesTabValid ? {} : invalidTabHeaderIcon;

  return (
    <>
      {isTabs ? (
        <TabView
          className={
            Object.keys(errors).some((k) => Object.keys(errors).includes(k))
              ? t('invalid')
              : ''
          }
          activeIndex={activeTab}
          onTabChange={(e: TabViewTabChangeParams) => {
            setActiveTab(e.index);
          }}
        >
          <TabPanel
            header={t('Sender')}
            headerClassName="data-cy-sender-tab"
            {...senderTabIcon}
          >
            <SenderTab
              hubs={hubsData}
              isConstant={constantFields.isSenderConstant}
              orderIndex={orderIndex}
              isSenderTabDisabled={isSenderTabDisabled}
            />
          </TabPanel>

          <TabPanel
            header={t('Recipient')}
            headerClassName="data-cy-recipient-tab"
            {...recipientTabIcon}
          >
            <RecipientTab
              // defaultValues={defaultValues}
              hubs={hubsData}
              isConstant={constantFields.isRecipientConstant}
              orderIndex={orderIndex}
              nearestRecipientHub={nearestRecipientHub}
              isNearestRecipientHubLoading={isLoadingNearestHub}
            />
          </TabPanel>

          <TabPanel
            headerStyle={isOrdererDisplayed ? {} : { display: 'none' }}
            header={t('Orderer')}
            headerClassName="data-cy-orderer-tab"
            {...ordererTabIcon}
          >
            <OrdererTab />
          </TabPanel>

          <TabPanel
            header={t('Attributes')}
            headerClassName="data-cy-attributes-tab"
          >
            <AttributesTab
              isTabs={isTabs}
              isEditDialog={isEditDialog}
              {...attributesTabIcon}
            />
            {!isEditDialog && (
              <div className="pickup-assignment">
                <PickupAssignmentTab />
              </div>
            )}
            <PaymentsTab isLoading={isCalculatorDataLoading} />
          </TabPanel>
        </TabView>
      ) : (
        <div className="p-d-flex p-flex-wrap">
          <div className={`tabs ${isOrdererDisplayed && `${active}-active`}`}>
            <div className="sender" onClick={() => setActive('sender')}>
              <div className="tab">
                <Panel header={senderHeader}>
                  <SenderTab
                    isWarehouseReception={isWarehouseReception}
                    hubs={hubsData}
                    isConstant={constantFields.isSenderConstant}
                    orderIndex={orderIndex}
                    isSenderTabDisabled={isSenderTabDisabled}
                  />
                </Panel>

                <Panel header={recipientHeader}>
                  <RecipientTab
                    // defaultValues={defaultValues}
                    isConstant={constantFields.isRecipientConstant}
                    orderIndex={orderIndex}
                    hubs={hubsData}
                    nearestRecipientHub={nearestRecipientHub}
                    isNearestRecipientHubLoading={isLoadingNearestHub}
                  />
                </Panel>
              </div>
            </div>
            <div onClick={() => setActive('orderer')} className="orderer">
              <Panel
                header={ordererHeader}
                className={'tab ' + (!isOrdererDisplayed && ' p-d-none')}
              >
                <OrdererTab
                  isConstant={constantFields.isOrdererConstant}
                  orderIndex={orderIndex}
                />
              </Panel>
              <Button
                type="button"
                label={t('Third party orderer')}
                icon="fas fa-plus"
                iconPos="left"
                onClick={() => {
                  setValue('orderer', 3);
                  setValue('klient_naracatel_id', '3');
                  setActive('orderer');
                }}
                className={
                  'toggle-orderer ' + (isOrdererDisplayed && ' p-d-none')
                }
              />
            </div>
          </div>

          <div className="attributes">
            <Panel header={attributesHeader}>
              <AttributesTab
                isConstant={constantFields.isAttributesConstant}
                orderIndex={orderIndex}
              />
            </Panel>
          </div>

          {!isEditDialog && (
            <div className="pickup-assignment">
              <PickupAssignmentTab />
            </div>
          )}

          <div className="payments">
            <PaymentsTab isLoading={isCalculatorDataLoading} />
          </div>
        </div>
      )}
    </>
  );
}

export default FormContainer;
